import type { RootState } from "~/app/store";
import type { CartDescriptorFor } from "~/features/cart/cart/pna/cartPna";
import { isBundleQuoteGroup, isProductGroup } from "~/features/cart/cart/models/CartData";
import type {
  BundleQuoteGroup,
  EntryGroup,
  OrderableGroup,
  ProductGroup,
  QuotableGroup,
} from "~/features/cart/cart/dto/CartResponseDTO";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import { selectLineItemsByGroupNumber } from "~/features/cart/cart/redux/selectors/cartSelectors";
import {
  selectBundleGroups,
  selectOrderableGroup,
  selectQuotableGroup,
} from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import { buildAlternateRows, type StoredAlternateRow } from "~/features/cart/cartV2/redux/cartAlternatesSlice";
import {
  buildProductTableViewModel,
  buildOptionalPartDescriptorMap,
} from "~/features/cart/cartV2/utils/buildProductTableViewModel";
import { selectResolvedProductGroup } from "~/features/cart/cartV2/utils/selectResolvedGroupedEntryV2";
import type { ProductTableViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";

const selectEntryGroupsByNumber = (state: RootState) => state.cart.entryGroupsByNumber;
const selectCartAlternatesState = (state: RootState) => state.cartAlternates;

function collectProductGroups(group: EntryGroup): ProductGroup[] {
  if (isProductGroup(group)) {
    return [group];
  }
  return group.children.flatMap(collectProductGroups);
}

function buildGroupTableId(group: EntryGroup): string {
  const quoteNumber = isBundleQuoteGroup(group) ? group.quoteNumber : "";
  return `table:${group.groupType}:${group.groupNumber}:${quoteNumber}`;
}

function buildTableViewModelFromGroup(args: {
  group: OrderableGroup | QuotableGroup | BundleQuoteGroup;
  mode: "edit" | "review";
  descriptorFor: CartDescriptorFor;
  storedRows: StoredAlternateRow[];
  entryGroupsByNumber: RootState["cart"]["entryGroupsByNumber"];
  lineItemsByGroupNumber: Record<number, LineItem[]>;
}): ProductTableViewModel {
  const { group, mode, descriptorFor, storedRows, entryGroupsByNumber, lineItemsByGroupNumber } = args;

  const tableId = buildGroupTableId(group);
  const productGroups = collectProductGroups(group);
  const resolvedProductGroups = productGroups.map((pg) =>
    selectResolvedProductGroup(pg, lineItemsByGroupNumber, descriptorFor),
  );

  const productGroupNumbers = productGroups.map((pg) => pg.groupNumber);
  const alternateRows = buildAlternateRows({ tableId, storedRows, productGroupNumbers });

  const optionalPartDescriptors = buildOptionalPartDescriptorMap(descriptorFor, resolvedProductGroups);

  return buildProductTableViewModel({
    group,
    mode,
    resolvedProductGroups,
    lineItemsByGroupNumber,
    optionalPartDescriptors,
    alternateRows,
    entryGroupsByNumber,
  });
}

const emptyTableViewModel = (mode: "edit" | "review"): ProductTableViewModel => ({
  tableId: "table:empty",
  mode,
  groupType: "ORDERABLE" as ProductTableViewModel["groupType"],
  rows: [],
});

export const selectOrderablesViewModel =
  (descriptorFor: CartDescriptorFor) =>
  (state: RootState): ProductTableViewModel => {
    const orderableGroup = selectOrderableGroup(state);
    if (!orderableGroup) {
      return emptyTableViewModel("edit");
    }

    const tableId = buildGroupTableId(orderableGroup);
    const storedRows = state.cartAlternates.rowsByTableId[tableId] ?? [];

    return buildTableViewModelFromGroup({
      group: orderableGroup,
      mode: "edit",
      descriptorFor,
      storedRows,
      entryGroupsByNumber: state.cart.entryGroupsByNumber,
      lineItemsByGroupNumber: selectLineItemsByGroupNumber(state),
    });
  };

export const selectCheckoutOrderablesViewModel =
  (descriptorFor: CartDescriptorFor) =>
  (state: RootState): ProductTableViewModel => {
    const orderableGroup = selectOrderableGroup(state);
    if (!orderableGroup) {
      return emptyTableViewModel("review");
    }

    return buildTableViewModelFromGroup({
      group: orderableGroup,
      mode: "review",
      descriptorFor,
      storedRows: [],
      entryGroupsByNumber: state.cart.entryGroupsByNumber,
      lineItemsByGroupNumber: selectLineItemsByGroupNumber(state),
    });
  };

export const selectQuotablesViewModel =
  (descriptorFor: CartDescriptorFor) =>
  (state: RootState): ProductTableViewModel => {
    const quotableGroup = selectQuotableGroup(state);
    if (!quotableGroup) {
      return emptyTableViewModel("review");
    }

    return buildTableViewModelFromGroup({
      group: quotableGroup,
      mode: "review",
      descriptorFor,
      storedRows: [],
      entryGroupsByNumber: state.cart.entryGroupsByNumber,
      lineItemsByGroupNumber: selectLineItemsByGroupNumber(state),
    });
  };

export const selectBundleViewModels =
  (descriptorFor: CartDescriptorFor) =>
  (state: RootState): ProductTableViewModel[] => {
    const bundleGroups = selectBundleGroups(state);
    const entryGroupsByNumber = state.cart.entryGroupsByNumber;
    const lineItemsByGroupNumber = selectLineItemsByGroupNumber(state);

    return bundleGroups.map((bundle) =>
      buildTableViewModelFromGroup({
        group: bundle,
        mode: "edit",
        descriptorFor,
        storedRows: [],
        entryGroupsByNumber,
        lineItemsByGroupNumber,
      }),
    );
  };

export const selectCheckoutBundleViewModels =
  (descriptorFor: CartDescriptorFor) =>
  (state: RootState): ProductTableViewModel[] => {
    const bundleGroups = selectBundleGroups(state);
    const entryGroupsByNumber = state.cart.entryGroupsByNumber;
    const lineItemsByGroupNumber = selectLineItemsByGroupNumber(state);

    return bundleGroups.map((bundle) =>
      buildTableViewModelFromGroup({
        group: bundle,
        mode: "review",
        descriptorFor,
        storedRows: [],
        entryGroupsByNumber,
        lineItemsByGroupNumber,
      }),
    );
  };
